import {
  calculateExtendedBounds,
  calculateOptimalViewBox,
  type ExtendedBounds,
} from '@alchemiesolns/svg-interactive';
import { MOLECULE_FIXED_VIEWBOX_WORLD_HEIGHT } from '../constants/reactionScheme';

/** Map chemistry y-up bounds from `calculateExtendedBounds` to SVG y-down (same as useSVGGeneration). */
function flipExtendedBoundsChemYToSvg(bounds: ExtendedBounds): ExtendedBounds {
  return {
    minX: bounds.minX,
    maxX: bounds.maxX,
    minY: -bounds.maxY,
    maxY: -bounds.minY,
    width: bounds.width,
    height: bounds.height,
  };
}

function calculateSVGExtendedBounds(
  config: string,
  includeExtraSpace: boolean = true,
  minPadding: number = 2.0
): ExtendedBounds {
  return flipExtendedBoundsChemYToSvg(
    calculateExtendedBounds(config, includeExtraSpace, minPadding)
  );
}

/** Uniform inset (negative) or outset (positive) around bounds in SVG user units. */
export function expandBoundsByPadding(bounds: ExtendedBounds, padding: number): ExtendedBounds {
  if (!Number.isFinite(padding) || padding === 0) {
    return bounds;
  }
  const minX = bounds.minX - padding;
  const maxX = bounds.maxX + padding;
  const minY = bounds.minY - padding;
  const maxY = bounds.maxY + padding;
  return {
    minX,
    maxX,
    minY,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

export type ViewBoxScaleMode = 'fixed' | 'fit';

/**
 * Horizontal placement uses **tight** bounds (`includeExtraSpace: false`). **Extended** bounds use
 * symbol-aware label rects + variable decoration slack from `@alchemiesolns/svg-interactive`.
 */
const ALIGN_EDGE_PAD = 0.2;
/** Minimum gap so extended decorations (labels, lone pairs) still fit inside the fixed window. */
const CONTAIN_PAD = 0.35;

/** Tight geometry in SVG coords: no worst-case labels / no 3.0 decoration slack. */
const TIGHT_ATOM_PADDING = 0.45;

function clamp(x: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, x));
}

/**
 * Fixed-scale paths use a synthetic rectangle with the **same aspect ratio** as the SVG slot.
 * Horizontal position targets **tight** bounds (geometry + small pad).
 * For **`left`**, we nudge the window if needed so `extended` is not clipped on the left.
 * For **`right`** (reactant, arrow on the right), we do **not** apply that nudge: shifting
 * `minX` to clear `extended.minX` would move the whole window right and add empty space on the
 * arrow side (molecule looks too far from the arrow). Prefer hugging the tight right edge;
 * extended may clip on the far side.
 */
function syntheticBoundsFixed(
  extended: ExtendedBounds,
  tight: ExtendedBounds,
  fixedWidth: number,
  fixedHeight: number,
  alignment: 'left' | 'center' | 'right'
): ExtendedBounds {
  const centerY = extended.minY + extended.height / 2;
  const minY = centerY - fixedHeight / 2;
  const maxY = centerY + fixedHeight / 2;

  const c = CONTAIN_PAD;
  const a = ALIGN_EDGE_PAD;
  const lo = extended.maxX + c - fixedWidth;
  const hi = extended.minX - c;

  let minX: number;
  let maxX: number;

  if (lo > hi) {
    const cx = extended.minX + extended.width / 2;
    minX = cx - fixedWidth / 2;
    maxX = minX + fixedWidth;
  } else if (alignment === 'center') {
    const cx = extended.minX + extended.width / 2;
    minX = clamp(cx - fixedWidth / 2, lo, hi);
    maxX = minX + fixedWidth;
  } else if (alignment === 'left') {
    minX = tight.minX - a;
    if (minX < extended.minX - c) {
      minX = extended.minX - c;
    }
    maxX = minX + fixedWidth;
  } else {
    // Right / arrow-side hug only — do not clamp minX to extended.minX (see module comment).
    minX = tight.maxX + a - fixedWidth;
    maxX = minX + fixedWidth;
  }

  return {
    minX,
    maxX,
    minY,
    maxY,
    width: fixedWidth,
    height: fixedHeight,
  };
}

/** Geometry-only horizontal extent (atoms ± small pad + bonds). */
function calculateSVGTightBounds(config: string): ExtendedBounds {
  return calculateSVGExtendedBounds(config, false, TIGHT_ATOM_PADDING);
}

/**
 * Compute a single viewBox for an individual molecule.
 * Used when only one molecule (reactant or product) is present.
 */
export function computeSingleViewBox(
  config: string,
  svgWidth: number,
  svgHeight: number,
  alignment: 'left' | 'center' | 'right',
  scaleMode: ViewBoxScaleMode = 'fixed',
  extraUserPaddingUnits: number = 0
): string {
  const bounds = expandBoundsByPadding(
    calculateSVGExtendedBounds(config, true, 2.0),
    extraUserPaddingUnits
  );
  if (scaleMode === 'fit') {
    return calculateOptimalViewBox(svgWidth, svgHeight, bounds, alignment);
  }
  const aspect = svgWidth / Math.max(1, svgHeight);
  const fixedWidth = MOLECULE_FIXED_VIEWBOX_WORLD_HEIGHT * aspect;
  const fixedHeight = MOLECULE_FIXED_VIEWBOX_WORLD_HEIGHT;

  const tight = calculateSVGTightBounds(config);
  const syntheticBounds = syntheticBoundsFixed(
    bounds,
    tight,
    fixedWidth,
    fixedHeight,
    alignment
  );

  return calculateOptimalViewBox(svgWidth, svgHeight, syntheticBounds, 'center');
}

/**
 * Compute normalized viewBoxes so that both molecules render at the same
 * physical scale. Uses identical synthetic frame width/height for both; in **fixed**
 * mode each frame is placed horizontally by `syntheticBoundsFixed` (edge-hugging
 * left/right/center). Because the synthetic bounds share the same dimensions,
 * both viewBoxes get the same width/height — hence the same scale.
 */
export function computeNormalizedViewBoxes(
  reactantConfig: string,
  productConfig: string,
  svgWidth: number,
  svgHeight: number,
  reactantAlignment: 'left' | 'center' | 'right',
  productAlignment: 'left' | 'center' | 'right',
  scaleMode: ViewBoxScaleMode = 'fixed',
  extraUserPaddingUnits: number = 0
): { reactantViewBox: string; productViewBox: string } {
  // Use SVG-coordinate bounds (with y-flip) to match what will be rendered
  const rBounds = expandBoundsByPadding(
    calculateSVGExtendedBounds(reactantConfig, true, 2.0),
    extraUserPaddingUnits
  );
  const pBounds = expandBoundsByPadding(
    calculateSVGExtendedBounds(productConfig, true, 2.0),
    extraUserPaddingUnits
  );

  if (scaleMode === 'fit') {
    const maxWidth = Math.max(rBounds.width, pBounds.width);
    const maxHeight = Math.max(rBounds.height, pBounds.height);
    const rCenterX = rBounds.minX + rBounds.width / 2;
    const rCenterY = rBounds.minY + rBounds.height / 2;
    const pCenterX = pBounds.minX + pBounds.width / 2;
    const pCenterY = pBounds.minY + pBounds.height / 2;
    const rSynthetic = {
      minX: rCenterX - maxWidth / 2,
      maxX: rCenterX + maxWidth / 2,
      minY: rCenterY - maxHeight / 2,
      maxY: rCenterY + maxHeight / 2,
      width: maxWidth,
      height: maxHeight,
    };
    const pSynthetic = {
      minX: pCenterX - maxWidth / 2,
      maxX: pCenterX + maxWidth / 2,
      minY: pCenterY - maxHeight / 2,
      maxY: pCenterY + maxHeight / 2,
      width: maxWidth,
      height: maxHeight,
    };
    return {
      reactantViewBox: calculateOptimalViewBox(svgWidth, svgHeight, rSynthetic, reactantAlignment),
      productViewBox: calculateOptimalViewBox(svgWidth, svgHeight, pSynthetic, productAlignment),
    };
  }

  const aspect = svgWidth / Math.max(1, svgHeight);
  const fixedWidth = MOLECULE_FIXED_VIEWBOX_WORLD_HEIGHT * aspect;
  const fixedHeight = MOLECULE_FIXED_VIEWBOX_WORLD_HEIGHT;

  const rTight = calculateSVGTightBounds(reactantConfig);
  const pTight = calculateSVGTightBounds(productConfig);
  const rSynthetic = syntheticBoundsFixed(
    rBounds,
    rTight,
    fixedWidth,
    fixedHeight,
    reactantAlignment
  );
  const pSynthetic = syntheticBoundsFixed(
    pBounds,
    pTight,
    fixedWidth,
    fixedHeight,
    productAlignment
  );
  return {
    reactantViewBox: calculateOptimalViewBox(svgWidth, svgHeight, rSynthetic, 'center'),
    productViewBox: calculateOptimalViewBox(svgWidth, svgHeight, pSynthetic, 'center'),
  };
}
